import { createUseStyles } from 'react-jss';
import { useSubscription } from '@autopylot-internal/state';
import { useInstance } from '../../support/ioc/useInstance';
import { MissionsState } from '../../missions/model/MissionsState.ts';
import { EditableMissionState } from '../../mission/model/EditableMissionState.ts';
import { RecentMissionItem } from './RecentMissionItem.tsx';
import { RevenueCatModel } from '../../paywall/revenue-cat/RevenueCatModel.ts';
import { PaywallDialogViewModel } from '../../paywall/PaywallDialogViewModel.tsx';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { useMemo, useState } from 'react';
import { RecentMissionsModal } from './RecentMissionsModal.tsx';
import { ArticleLink } from '../../app/intercom/ArticleLink.tsx';
import { HelpIcon } from '../../components/icons/Icons.tsx';
import { Spinner } from '../../components/spinner/Spinner.tsx';

const useStyles = createUseStyles({
    container: {
        marginTop: 12,
        padding: '0 12px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center'
    },
    title: {
        fontSize: 16,
        fontWeight: 600
    },
    helpLink: {
        '& a': {
            padding: 5,
            display: 'flex'
        }
    },
    moreLink: {
        fontSize: 14,
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        fontWeight: 500
    },
    emptyText: {
        fontSize: 14,
        color: 'var(--ion-color-medium)',
        marginTop: 8,
        lineHeight: '1.4'
    }
});

const MAX_VISIBLE_RECENTS = 2;

interface IRecentMissionsProps {
    onSelectMission: (mission: EditableMissionState) => void;
}

export const RecentMissions: React.FC<IRecentMissionsProps> = ({ onSelectMission }) => {
    const classes = useStyles();

    const missionsState = useInstance(MissionsState);
    useSubscription(() => missionsState);

    const revenueCat = useInstance(RevenueCatModel);
    const paywallDialog = useInstance(PaywallDialogViewModel);
    const tracker = useInstance(TrackingService);

    const recentMissions = useMemo(
        () => missionsState.getRecentMissions(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [missionsState.state.lastLoaded]
    );

    const isLoading = !missionsState.state.isFullyLoaded && recentMissions.length < MAX_VISIBLE_RECENTS;
    const visibleMissions = recentMissions.slice(0, MAX_VISIBLE_RECENTS);
    const hasMore = recentMissions.length > MAX_VISIBLE_RECENTS;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const onMoreClick = () => {
        tracker.track('Recent Missions More Tapped', () => ({ recentMissionsCount: recentMissions.length }));
        if (!revenueCat.hasUpgraded()) {
            paywallDialog.open({ triggerSource: 'recent missions' });
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <div className={classes.container} data-id="recent-missions-section">
            <div className={classes.header}>
                <div className={classes.titleGroup}>
                    <span className={classes.title}>Recents</span>
                    <span className={classes.helpLink}>
                        <ArticleLink articleKey="RECENT_MISSIONS"><HelpIcon size="small" color="primary" /></ArticleLink>
                    </span>
                </div>
                {hasMore && (
                    <button className={classes.moreLink} onClick={onMoreClick} data-id="recent-missions-more">
                        More
                    </button>
                )}
            </div>

            {!isLoading && visibleMissions.length === 0 && (
                <div className={classes.emptyText}>
                    No saved missions yet! Tap the screen to drop a pin or use the location search above.
                </div>
            )}

            {visibleMissions.map(mission => (
                <RecentMissionItem
                    key={mission.state.missionId}
                    mission={mission}
                    onSelect={(m) => {
                        tracker.track('Recent Mission Selected', () => ({ from: 'search', address: m.getAddress() }));
                        onSelectMission(m);
                    }}
                />
            ))}

            {isLoading && (
                <div style={{textAlign: 'center', marginTop: 12}}>
                    <Spinner size="small" />
                </div>
            )}

            <RecentMissionsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelectMission={onSelectMission}
            />
        </div>
    );
};
